import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import {
  filterSelectClass,
  formLabelClass,
  formSelectClass,
  selectDropdownOpenClass,
  selectDropdownOptionClass,
  selectDropdownPanelClass,
  selectDropdownPanelDropUpClass,
  selectDropdownPanelPortalClass,
} from './uiClasses.js';

const parseOptionChildren = (children) =>
  React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((child) => ({
      value: child.props.value ?? '',
      label: child.props.children ?? child.props.value ?? '',
      disabled: Boolean(child.props.disabled),
      className: child.props.className || '',
    }));

/**
 * Custom select dropdown — same width, rounded-xl trigger, panel opens directly below.
 * Drop-in replacement for native <select> (supports option children + synthetic onChange events).
 */
const SelectField = ({
  value = '',
  onChange,
  name,
  id,
  options,
  children,
  label,
  error = false,
  required = false,
  disabled = false,
  placeholder,
  variant = 'form',
  className = '',
  labelClassName = formLabelClass,
  emptyLabel,
  dropUp = false,
  triggerClassName = '',
  portal = false,
  searchable = false,
  searchPlaceholder = 'Search',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [portalStyle, setPortalStyle] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const generatedId = useId();
  const fieldId = id || name || generatedId;

  const parsedOptions = useMemo(() => {
    if (Array.isArray(options) && options.length > 0) return options;
    return parseOptionChildren(children);
  }, [options, children]);

  const visibleOptions = useMemo(() => {
    if (!searchable) return parsedOptions;
    const needle = query.trim().toLowerCase();
    if (!needle) return parsedOptions;
    return parsedOptions.filter((opt) => {
      const hay = String(
        opt.searchText
        || (typeof opt.label === 'string' ? opt.label : ''),
      ).toLowerCase();
      return hay.includes(needle);
    });
  }, [parsedOptions, query, searchable]);

  const selectedOption = parsedOptions.find((opt) => String(opt.value) === String(value));
  const displayLabel =
    selectedOption?.triggerLabel
    ?? selectedOption?.label
    ?? placeholder
    ?? emptyLabel
    ?? (variant === 'filter' ? 'All' : 'Select...');

  const hasErrorClass =
    error || (typeof className === 'string' && className.includes('border-red-400'));
  const resolvedTriggerClass = triggerClassName
    ? triggerClassName
    : variant === 'filter'
      ? filterSelectClass
      : formSelectClass(hasErrorClass);

  const wrapperWidthClass =
    typeof className === 'string'
      ? className
          .split(/\s+/)
          .filter((token) => /^(w-|min-w-|max-w-)/.test(token))
          .join(' ')
      : '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = containerRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open || !portal) {
      setPortalStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const openUp = dropUp || (spaceBelow < 140 && spaceAbove > spaceBelow);
      const maxHeight = Math.max(120, Math.min(288, openUp ? spaceAbove - gap : spaceBelow - gap));
      setPortalStyle({
        left: rect.left,
        width: rect.width,
        maxHeight,
        ...(openUp
          ? { top: 'auto', bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap, bottom: 'auto' }),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, portal, dropUp]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    if (!searchable) return undefined;
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open, searchable]);

  const emitChange = (newValue) => {
    onChange?.({ target: { name, value: newValue } });
  };

  const handleSelect = (optValue) => {
    if (disabled) return;
    emitChange(optValue);
    setOpen(false);
  };

  const renderList = () => {
    const list = (
      <ul
        ref={panelRef}
        role="listbox"
        className={
          portal
            ? selectDropdownPanelPortalClass
            : dropUp
              ? selectDropdownPanelDropUpClass
              : selectDropdownPanelClass
        }
        style={portal ? portalStyle || { visibility: 'hidden' } : undefined}
        aria-labelledby={fieldId}
      >
        {visibleOptions.length === 0 ? (
          <li className="px-3 py-2 text-sm text-gray-400">No matches</li>
        ) : null}
        {visibleOptions.map((opt) => {
          const isSelected = String(opt.value) === String(value);
          return (
            <li key={String(opt.value)} role="option" aria-selected={isSelected}>
              <button
                type="button"
                disabled={opt.disabled}
                onClick={() => !opt.disabled && handleSelect(opt.value)}
                className={`${selectDropdownOptionClass(isSelected)} ${opt.className || ''} disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {opt.label}
              </button>
            </li>
          );
        })}
      </ul>
    );
    return portal && typeof document !== 'undefined' ? createPortal(list, document.body) : list;
  };

  return (
    <div
      className={`relative min-w-0 ${wrapperWidthClass || 'w-full'} ${open ? 'z-[80]' : ''}`}
      ref={containerRef}
      style={open ? { zIndex: 80 } : undefined}
    >
      {label ? (
        <label htmlFor={fieldId} className={labelClassName}>
          {label} {required ? <span className="text-red-500">*</span> : null}
        </label>
      ) : null}

      <div
        ref={triggerRef}
        className={`${resolvedTriggerClass} flex w-full items-center justify-between gap-2 text-left !leading-normal ${
          disabled ? 'cursor-not-allowed opacity-60' : ''
        } ${open ? selectDropdownOpenClass : ''}`}
      >
        {open && searchable ? (
          <input
            ref={searchRef}
            id={fieldId}
            name={name}
            type="text"
            value={query}
            disabled={disabled}
            placeholder={searchPlaceholder}
            aria-haspopup="listbox"
            aria-expanded={open}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setOpen(false);
            }}
            className="min-w-0 flex-1 bg-transparent text-sm font-medium leading-normal text-gray-800 outline-none placeholder:font-normal placeholder:text-gray-400"
          />
        ) : (
          <button
            type="button"
            id={searchable ? undefined : fieldId}
            name={searchable ? undefined : name}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              setOpen((prev) => (searchable ? true : !prev));
            }}
            aria-haspopup="listbox"
            aria-expanded={open}
            title={typeof displayLabel === 'string' ? displayLabel : undefined}
            className={`min-w-0 flex-1 bg-transparent text-left outline-none ${
              variant === 'filter'
                ? 'truncate whitespace-nowrap'
                : 'whitespace-normal break-words line-clamp-2'
            } ${
              !selectedOption && (placeholder || emptyLabel) ? 'text-gray-500 font-normal' : ''
            }`}
          >
            {displayLabel}
          </button>
        )}
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label={open ? 'Close' : 'Open'}
          onClick={() => !disabled && setOpen((prev) => !prev)}
          className="shrink-0"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${
              open ? 'rotate-180 text-blue-500' : 'text-gray-500'
            }`}
            aria-hidden="true"
          />
        </button>
      </div>

      {open ? renderList() : null}
    </div>
  );
};

export default SelectField;
